import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useT } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass = "w-full bg-slate-50 border border-slate-300 rounded-none focus-visible:border-[#1F3460] focus-visible:ring-1 focus-visible:ring-[#1F3460] focus-visible:ring-offset-0 h-12 px-4 text-[#1F3460] placeholder:text-slate-400";

export default function Contact() {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("toast.contact_fill"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("toast.contact_sent"));
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || t("toast.contact_fail");
      toast.error(typeof detail === "string" ? detail : t("toast.contact_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-slate-50 py-20 md:py-28 px-5 md:px-8 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto">
          <div className="overline">{t("contact.hero.overline")}</div>
          <h1 className="font-display text-5xl md:text-7xl font-black text-[#1F3460] mt-4 leading-[0.95]">
            {t("contact.hero.title1")} <br />
            <span className="text-[#6FA8DC]">{t("contact.hero.title2")}</span>
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-28 px-5 md:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline">{t("contact.info.overline")}</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#1F3460] mt-3 leading-tight">{t("contact.info.title")}</h2>
            <ul className="mt-10 space-y-6">
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#1F3460] text-white flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#6FA8DC]" /></div>
                <div>
                  <div className="overline">{t("contact.info.email")}</div>
                  <a href="mailto:brightflinspections@gmail.com" data-testid="contact-info-email" className="font-display text-lg md:text-xl font-black text-[#1F3460] hover:text-[#6FA8DC] transition-colors block mt-1 break-all">brightflinspections@gmail.com</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#1F3460] text-white flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-[#6FA8DC]" /></div>
                <div>
                  <div className="overline">{t("contact.info.phone")}</div>
                  <a href="tel:+12396927666" data-testid="contact-info-phone" className="font-display text-lg md:text-xl font-black text-[#1F3460] hover:text-[#6FA8DC] transition-colors block mt-1">(239) 692-7666</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#1F3460] text-white flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#6FA8DC]" /></div>
                <div>
                  <div className="overline">{t("contact.info.area_label")}</div>
                  <div data-testid="contact-info-area" className="font-display text-lg md:text-xl font-black text-[#1F3460] mt-1">{t("contact.info.area")}</div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#1F3460] text-white flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-[#6FA8DC]" /></div>
                <div>
                  <div className="overline">{t("contact.info.hours_label")}</div>
                  <div data-testid="contact-info-hours" className="font-display text-lg md:text-xl font-black text-[#1F3460] mt-1">{t("contact.info.hours")}</div>
                </div>
              </li>
            </ul>
          </div>

          <div className="md:col-span-7">
            <div className="border border-[#E2E8F0] p-7 md:p-10 bg-white">
              {done ? (
                <div data-testid="contact-success" className="py-6">
                  <CheckCircle2 className="w-12 h-12 text-[#6FA8DC]" />
                  <h3 className="font-display text-3xl font-black text-[#1F3460] mt-4">{t("contact.success.title")}</h3>
                  <p className="mt-3 text-[#5E6B85]">{t("contact.success.body_pre")}{form.email}{t("contact.success.body_post")}</p>
                  <button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", message: "" }); }} data-testid="contact-send-another" className="btn-outline mt-7">
                    {t("contact.success.another")} <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5" noValidate>
                  <div className="overline">{t("contact.form.overline")}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="c-name" className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("contact.form.name")}</Label>
                      <Input id="c-name" data-testid="contact-name" className={inputClass} placeholder="Jane Doe" value={form.name} onChange={(e) => update("name", e.target.value)} required />
                    </div>
                    <div>
                      <Label htmlFor="c-phone" className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("contact.form.phone")}</Label>
                      <Input id="c-phone" data-testid="contact-phone" className={inputClass} placeholder="(555) 123-4567" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="c-email" className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("contact.form.email")}</Label>
                    <Input id="c-email" type="email" data-testid="contact-email" className={inputClass} placeholder="you@example.com" value={form.email} onChange={(e) => update("email", e.target.value)} required />
                  </div>
                  <div>
                    <Label htmlFor="c-message" className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">{t("contact.form.message")}</Label>
                    <Textarea id="c-message" data-testid="contact-message" rows={6} placeholder={t("contact.form.message_ph")} className="w-full bg-slate-50 border border-slate-300 rounded-none focus-visible:border-[#1F3460] focus-visible:ring-1 focus-visible:ring-[#1F3460] focus-visible:ring-offset-0 px-4 py-3 text-[#1F3460] placeholder:text-slate-400" value={form.message} onChange={(e) => update("message", e.target.value)} required />
                  </div>
                  <button type="submit" data-testid="contact-submit" disabled={submitting} className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed">
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("contact.form.sending")}</> : <>{t("contact.form.submit")} <ArrowUpRight className="w-4 h-4" /></>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
